import * as React from 'react';
import type { TFunction } from 'i18next';
import type { CellEntry } from '@ooh/contracts';
import {
  buildEntryDeadlineTooltipLabel,
  CellStateIcon,
  getCellColorClasses,
  getCellStateLabel,
  getHatchStyle,
  type CellPopoverLabels,
  type LegendItem,
  type AvailabilityPeriod,
  type CellState,
} from '../../../availability';

export type InventoryAvailabilityPeriod = AvailabilityPeriod & {
  start: string;
  end: string;
};

/**
 * Single source of truth for every `CellState` variant that also exists as an
 * `InventoryOccupancyState` (excludes 'free', which has no occupancy range). Exported as a
 * literal tuple — rather than only a derived type — so `queries.ts`'s
 * `INVENTORY_OCCUPANCY_STATES` runtime `Set` can be built from the SAME list `InventoryOccupancyState`
 * is typed from, instead of hand-duplicating the literals with no exhaustiveness check.
 */
export const INVENTORY_OCCUPANCY_STATE_VALUES = [
  'option-other',
  'option-own',
  'reserved-other-soft',
  'reserved-other-hard',
  'reserved-own-soft',
  'reserved-own-hard',
  'sold',
  'unknown',
  'unavailable',
] as const satisfies readonly CellState[];

export type InventoryOccupancyState = (typeof INVENTORY_OCCUPANCY_STATE_VALUES)[number];

/**
 * Compile-time exhaustiveness guard — the reverse direction of the `as const satisfies
 * readonly CellState[]` annotation above. That annotation only checks every element of
 * `INVENTORY_OCCUPANCY_STATE_VALUES` is a valid `CellState` (one-way, subset check); it does
 * NOT fail to compile if a `CellState` member is OMITTED from the array. This conditional type
 * resolves to a tuple (never assignable to `true`) whenever some non-`free` `CellState` member
 * isn't covered by `InventoryOccupancyState`, so assigning `true` below fails to compile in
 * that case — catching the exact gap that would otherwise let an omitted state silently fall
 * through `getInventoryCellStateForPeriod`'s `OCCUPANCY_STATE_PRECEDENCE` walk and render as
 * `free`.
 */
type _AssertEveryOccupancyCellStateCovered =
  Exclude<CellState, 'free'> extends InventoryOccupancyState
    ? true
    : [
        'CellState member missing from INVENTORY_OCCUPANCY_STATE_VALUES:',
        Exclude<Exclude<CellState, 'free'>, InventoryOccupancyState>,
      ];
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const _occupancyStateCoverageCheck: _AssertEveryOccupancyCellStateCovered = true;

export interface InventoryOccupancyRange {
  start: string;
  end: string;
  state: InventoryOccupancyState;
  ownerHint: string;
  /**
   * The dominant entry's raw `offerTitle` — preserved ALONGSIDE `ownerHint` (never
   * merged into it) so `getInventoryOwnerOfferTitleForPeriod` can scope
   * `extractShortOwnerLabel`'s inline short-label extraction to the offer's own text,
   * never a `clientName`/`holderName` parenthesis picked up from `ownerHint`'s joined
   * string (CU-86cbh0jhy, follow-up). Undefined when the dominant entry carries no
   * offer link (base-state, or a hold not linked to any offer).
   */
  offerTitle?: string;
  /**
   * True when the dominant entry backing this range is a conditional sale
   * (HARD_RESERVATION with `conditional: true`). Drives the "conditional-sale red
   * font" treatment on the merged rich cell's inline label, mirroring the old
   * full-availability grid's `MultiStackCellContent` red-font rule. Optional so
   * existing occupancy fixtures/callers that don't care about conditional sales
   * stay unaffected.
   */
  conditional?: boolean;
  /** Raw reservation status, needed to tell an expiring hold apart from OPTION/SOLD (never expire) when formatting `deadlineLabel`. Undefined for base-state/window ranges. */
  reservationStatus?: CellEntry['status'];
  /** Raw expiry deadline (already the effective instant) — null/undefined mean no deadline. */
  ttlExpiresAt?: string | null;
}

export type InventoryOccupancyByRowId = Record<string, InventoryOccupancyRange[]>;

export function rangesOverlap(startA: string, endA: string, startB: string, endB: string): boolean {
  return startA <= endB && endA >= startB;
}

/**
 * Single source of truth for occupancy precedence — sold > reserved-other-hard >
 * reserved-other-soft > reserved-own-hard > reserved-own-soft > option-other >
 * option-own > unavailable > unknown. Own (mine) variants rank directly below their
 * "other" counterpart at the same reservation-status tier (mirroring STATUS_PRECEDENCE's
 * HARD/SOFT_RESERVATION > OPTION ordering) — an overlapping reservation held by someone
 * else is the more attention-worthy state to surface first. Hard ranks above soft within
 * each ownership tier, mirroring the canonical STATUS_PRECEDENCE ladder's HARD_RESERVATION
 * > SOFT_RESERVATION ordering (`packages/contracts/src/schemas/availability.ts`).
 * `getInventoryCellStateForPeriod` (cell background/icon) and `resolveDominantOccupancyRange`
 * (owner-hint/conditional-sale styling) both derive from this ONE ordering, so they can
 * never disagree about which overlapping range dominates a given cell.
 */
const OCCUPANCY_STATE_PRECEDENCE = [
  'sold',
  'reserved-other-hard',
  'reserved-other-soft',
  'reserved-own-hard',
  'reserved-own-soft',
  'option-other',
  'option-own',
  'unavailable',
  'unknown',
] as const satisfies readonly InventoryOccupancyState[];

/**
 * Compile-time exhaustiveness guard, the reverse direction of the `as const satisfies
 * readonly InventoryOccupancyState[]` annotation above — see
 * `_AssertEveryOccupancyCellStateCovered`'s doc comment for why the one-way `satisfies`
 * check alone doesn't catch an omitted member. Fails to compile if some
 * `InventoryOccupancyState` is missing from `OCCUPANCY_STATE_PRECEDENCE`, which would
 * otherwise make `resolveDominantOccupancyRange`'s precedence walk silently skip that
 * state's overlapping ranges.
 */
type _AssertEveryOccupancyStateRanked =
  InventoryOccupancyState extends (typeof OCCUPANCY_STATE_PRECEDENCE)[number]
    ? true
    : [
        'InventoryOccupancyState member missing from OCCUPANCY_STATE_PRECEDENCE:',
        Exclude<InventoryOccupancyState, (typeof OCCUPANCY_STATE_PRECEDENCE)[number]>,
      ];
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const _occupancyPrecedenceCoverageCheck: _AssertEveryOccupancyStateRanked = true;

/**
 * Resolves the single dominant range for a cell by walking `OCCUPANCY_STATE_PRECEDENCE`
 * in order and returning the first state that has an overlapping range. Backs both
 * `getInventoryCellStateForPeriod` (state) and `getInventoryOwnerHintForPeriod`/
 * `getInventoryOwnerConditionalForPeriod` (hint/conditional) so a cell's displayed
 * state, owner-hint text, and conditional-sale styling always describe the SAME range.
 */
function resolveDominantOccupancyRange(
  rowId: string,
  period: InventoryAvailabilityPeriod,
  occupancyByRowId: InventoryOccupancyByRowId,
): InventoryOccupancyRange | undefined {
  const ranges = occupancyByRowId[rowId] ?? [];
  for (const state of OCCUPANCY_STATE_PRECEDENCE) {
    const match = ranges.find(
      (range) =>
        range.state === state && rangesOverlap(period.start, period.end, range.start, range.end),
    );
    if (match !== undefined) return match;
  }
  return undefined;
}

export function getInventoryCellStateForPeriod(
  rowId: string,
  period: InventoryAvailabilityPeriod,
  occupancyByRowId: InventoryOccupancyByRowId,
): CellState {
  return resolveDominantOccupancyRange(rowId, period, occupancyByRowId)?.state ?? 'free';
}

export function getInventoryOwnerHintForPeriod(
  rowId: string,
  period: InventoryAvailabilityPeriod,
  occupancyByRowId: InventoryOccupancyByRowId,
): string | undefined {
  return resolveDominantOccupancyRange(rowId, period, occupancyByRowId)?.ownerHint;
}

/**
 * The dominant range's raw `offerTitle` — see `InventoryOccupancyRange.offerTitle`'s doc
 * comment. Passed to `AvailabilityCellVisual`'s `offerTitle` prop ALONGSIDE (never
 * instead of) `getInventoryOwnerHintForPeriod`'s `ownerHint`, so the inline short label
 * can be scoped to the offer's own text (CU-86cbh0jhy, follow-up).
 */
export function getInventoryOwnerOfferTitleForPeriod(
  rowId: string,
  period: InventoryAvailabilityPeriod,
  occupancyByRowId: InventoryOccupancyByRowId,
): string | undefined {
  return resolveDominantOccupancyRange(rowId, period, occupancyByRowId)?.offerTitle;
}

/**
 * Whether the dominant range `getInventoryOwnerHintForPeriod` resolves the owner hint
 * from is a conditional sale — see `resolveDominantOccupancyRange`.
 */
export function getInventoryOwnerConditionalForPeriod(
  rowId: string,
  period: InventoryAvailabilityPeriod,
  occupancyByRowId: InventoryOccupancyByRowId,
): boolean {
  return resolveDominantOccupancyRange(rowId, period, occupancyByRowId)?.conditional === true;
}

/**
 * The dominant range's expiry deadline, pre-formatted with the caller-supplied
 * translated prefix — see `buildEntryDeadlineTooltipLabel`. Undefined when the range's
 * status never expires, has no deadline set, or carries no reservation at all.
 */
export function getInventoryDeadlineLabelForPeriod(
  rowId: string,
  period: InventoryAvailabilityPeriod,
  occupancyByRowId: InventoryOccupancyByRowId,
  expiresLabelPrefix: string,
): string | undefined {
  const range = resolveDominantOccupancyRange(rowId, period, occupancyByRowId);
  if (!range?.reservationStatus) return undefined;
  return buildEntryDeadlineTooltipLabel(
    { status: range.reservationStatus, ttlExpiresAt: range.ttlExpiresAt },
    expiresLabelPrefix,
  );
}

export function buildInventoryAvailabilityGrid(
  rowIds: string[],
  periods: InventoryAvailabilityPeriod[],
  occupancyByRowId: InventoryOccupancyByRowId,
): Record<string, Record<string, CellState>> {
  const grid: Record<string, Record<string, CellState>> = {};
  for (const rowId of rowIds) {
    grid[rowId] = Object.fromEntries(
      periods.map((period) => [
        period.id,
        getInventoryCellStateForPeriod(rowId, period, occupancyByRowId),
      ]),
    );
  }
  return grid;
}

/**
 * Returns CellPopoverLabels suitable for a read-only external popover.
 * All action labels are empty strings; only the "occupied by other" message
 * and close button label are populated. Used by inventory and detail routes
 * that render their own CellPopover with onReserve=undefined.
 */
export function buildReadOnlyCellPopoverLabels(
  t: (key: string, options?: Record<string, unknown>) => string,
  occupiedByOtherKey: string,
  closeBtnKey: string,
  unavailableWindowMessageKey: string,
): CellPopoverLabels {
  return {
    reserveMulti: () => '',
    reserveSingle: () => '',
    releaseLabel: '',
    occupiedByOther: t(occupiedByOtherKey),
    unavailableWindowMessage: (period: string) => t(unavailableWindowMessageKey, { period }),
    autoSnapWarning: () => '',
    addOptionBtn: '',
    reserveBtn: '',
    releaseBtn: '',
    cancelBtn: '',
    closeBtn: t(closeBtnKey),
    ttlCountdownLabel: '',
  };
}

/**
 * Status legend entries shared by every surface that renders the shared availability
 * cell helpers (catalog results view sub-toolbar, single-unit detail card) — keeps
 * the status vocabulary consistent instead of each call site maintaining its own
 * copy of the same six state/label pairs.
 */
const AVAILABILITY_LEGEND_STATES: CellState[] = [
  'free',
  'unknown',
  'option-other',
  'reserved-other-soft',
  'reserved-other-hard',
  'sold',
  'unavailable',
];

export function buildAvailabilityLegendItems(tCommon: TFunction<'common'>): LegendItem[] {
  return AVAILABILITY_LEGEND_STATES.map((state) => ({
    state,
    label: getCellStateLabel(state, tCommon),
  }));
}

export function InventoryAvailabilityCell({
  state,
  label,
  ownerHint,
}: {
  state: CellState;
  label: string;
  ownerHint?: string;
}) {
  const title = ownerHint ? `${label}: ${ownerHint}` : label;
  return (
    <div
      title={title}
      className={`w-full h-full border-0 rounded-none flex items-center justify-center ${getCellColorClasses(state)}`}
      style={getHatchStyle(state)}
    >
      <CellStateIcon state={state} />
    </div>
  );
}
